import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { Award, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { listCommodityQualityGrades, listCommodityQualityGradesReset } from "../../actions";
import { SortMenu } from "@/components/SortMenu";
import { DataTable } from "@/components/ui/data-table";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { DataTablePagination } from "@/components/ui/table-pagination";
import { displayErrorMessage, getCountryObjectFromCache } from "@/utils";
import { getCurrentLanguageLabel } from "@/utils/i18n";

import { createColumns, QUALITY_GRADE_SORT_OPTIONS } from "./columns";
import { type QualityGrade } from "./types";

const PAGE_SIZE = 20;

interface RootState {
  listCommodityQualityGrades: {
    isFetching: boolean;
    errorMessage: string;
    data: { results: QualityGrade[] };
  };
}

interface StateProps {
  errorMessage: string;
  isFetching: boolean;
  qualityGrades: QualityGrade[];
}

interface DispatchProps {
  listCommodityQualityGrades: () => void;
  listCommodityQualityGradesReset: () => void;
}

type QualityGradesProps = StateProps & DispatchProps;

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = <TResult,>(dispatch: AppDispatch, action: unknown): TResult =>
  dispatch(action as UnknownAction) as TResult;

const normalize = (value: string) => value.trim().toLocaleLowerCase();

export const QualityGrades = ({
  errorMessage,
  isFetching,
  listCommodityQualityGrades,
  listCommodityQualityGradesReset,
  qualityGrades,
}: QualityGradesProps) => {
  const { i18n, t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    listCommodityQualityGrades();

    return () => {
      listCommodityQualityGradesReset();
    };
  }, [listCommodityQualityGrades, listCommodityQualityGradesReset]);

  const selectedSort = sorting[0];
  const selectedSortColumn = selectedSort?.id ?? "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";

  const onSortColumnChange = (sortColumn: string) => {
    setSorting([{ id: sortColumn, desc: selectedSortDirection === "desc" }]);
  };

  const onSortDirectionChange = (sortDirection: string) => {
    setSorting([
      {
        id: selectedSortColumn || QUALITY_GRADE_SORT_OPTIONS[0].id,
        desc: sortDirection === "desc",
      },
    ]);
  };

  const countryName = getCountryObjectFromCache()?.name ?? "";
  const languageLabel = useMemo(
    () => getCurrentLanguageLabel(i18n.language) as string,
    [i18n.language]
  );

  const columns = useMemo(
    () =>
      createColumns(t, {
        languageCode: i18n.language,
        languageLabel,
      }),
    [i18n.language, languageLabel, t]
  );

  const filteredQualityGrades = useMemo(() => {
    const query = normalize(searchTerm);

    if (!query) {
      return qualityGrades;
    }

    return qualityGrades.filter(qualityGrade =>
      [qualityGrade.name, qualityGrade.code, qualityGrade.name_translations?.[i18n.language]].some(
        value => normalize(String(value ?? "")).includes(query)
      )
    );
  }, [qualityGrades, searchTerm, i18n.language]);

  useEffect(() => {
    setPagination(current => (current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }));
  }, [sorting, searchTerm]);

  useEffect(() => {
    setPagination(current => {
      const pageCount = Math.max(1, Math.ceil(filteredQualityGrades.length / current.pageSize));
      const nextPageIndex = Math.min(current.pageIndex, pageCount - 1);

      return nextPageIndex === current.pageIndex
        ? current
        : { ...current, pageIndex: nextPageIndex };
    });
  }, [filteredQualityGrades.length]);

  const table = useReactTable({
    columns,
    data: filteredQualityGrades,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: row => String(row.id),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: { pagination, sorting },
  });

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-x-2">
          <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 bg-blue-100 text-blue-600">
            <Award />
          </span>
          <h1 className="font-semibold text-2xl">
            {t("qualityGrades.title", { country: countryName })}
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">{t("qualityGrades.description")}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <InputGroup className="w-full max-w-xs sm:w-80">
          <InputGroupAddon>
            <Search className="size-4" aria-hidden="true" />
          </InputGroupAddon>
          <InputGroupInput
            id="quality-grade-name-input"
            type="search"
            value={searchTerm}
            className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
            placeholder={t("qualityGrades.searchPlaceholder")}
            aria-label={t("qualityGrades.searchPlaceholder")}
            onChange={event => setSearchTerm(event.target.value)}
          />
          {searchTerm && (
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={t("qualityGrades.clearFilter")}
                onClick={() => setSearchTerm("")}
              >
                <X className="size-3.5" aria-hidden="true" />
              </InputGroupButton>
            </InputGroupAddon>
          )}
        </InputGroup>
        <SortMenu
          options={QUALITY_GRADE_SORT_OPTIONS}
          sortColumn={selectedSortColumn}
          sortDirection={selectedSortDirection}
          onSortColumnChange={onSortColumnChange}
          onSortDirectionChange={onSortDirectionChange}
        />
      </div>

      {errorMessage ? (
        displayErrorMessage(errorMessage)
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-background">
          <DataTable emptyMessage={t("qualityGrades.empty")} isLoading={isFetching} table={table} />
          <DataTablePagination table={table} />
        </div>
      )}
    </main>
  );
};

export const mapStateToProps = (state: RootState): StateProps => ({
  errorMessage: state.listCommodityQualityGrades.errorMessage,
  isFetching: state.listCommodityQualityGrades.isFetching,
  qualityGrades: state.listCommodityQualityGrades.data.results,
});

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  listCommodityQualityGrades: () => dispatchAction(dispatch, listCommodityQualityGrades()),
  listCommodityQualityGradesReset: () =>
    dispatchAction(dispatch, listCommodityQualityGradesReset()),
});

export const ConnectedQualityGrades = connect(mapStateToProps, mapDispatchToProps)(QualityGrades);
